import {
  Badge,
  Button,
  createSortableColumn,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  type TanstackReactTable,
} from "@devhop/ui";
import type { ReactNode } from "react";
import { Copy, Eye, FileArchive, MoreVertical, Pencil, Trash2 } from "lucide-react";
import type { CompanyPolicyListItem } from "@/modules/hr/company-policy/domain/types";

function formatDate(s: string) {
  try {
    return new Date(s).toLocaleDateString("lo-LA", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return s;
  }
}

function statusBadgeVariant(
  status: CompanyPolicyListItem["status"],
): "default" | "secondary" | "outline" {
  switch (status) {
    case "active":
      return "default";
    case "archived":
      return "secondary";
    default:
      return "outline";
  }
}

function statusLabel(status: CompanyPolicyListItem["status"]): string {
  switch (status) {
    case "draft":
      return "ຮ່າງ";
    case "active":
      return "ໃຊ້ງານ";
    case "archived":
      return "ເກັບໄວ້";
    default:
      return status;
  }
}

type PolicyTableProps = {
  data: CompanyPolicyListItem[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onView?: (row: CompanyPolicyListItem) => void;
  onEdit: (row: CompanyPolicyListItem) => void;
  onDelete?: (row: CompanyPolicyListItem) => void;
  onArchive?: (row: CompanyPolicyListItem) => void;
  onDuplicate?: (row: CompanyPolicyListItem) => void;
};

export function PolicyTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onView,
  onEdit,
  onDelete,
  onArchive,
  onDuplicate,
}: PolicyTableProps) {
  const columns: TanstackReactTable.ColumnDef<CompanyPolicyListItem>[] = [
    createSortableColumn<CompanyPolicyListItem>("title", "ຊື່ນະໂຍບາຍ", {
      size: 220,
      cell: ({ row }) => (
        <div>
          <div>
            {onView ? (
              <button
                type="button"
                className="font-medium text-primary hover:underline"
                onClick={() => onView(row.original)}
              >
                {row.original.title}
              </button>
            ) : (
              <span className="font-medium">{row.original.title}</span>
            )}
          </div>
          {row.original.code && (
            <div className="text-muted-foreground text-xs">{row.original.code}</div>
          )}
        </div>
      ),
    }),
    createSortableColumn<CompanyPolicyListItem>("category", "ປະເພດ", {
      size: 140,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.category ?? "—"}
        </span>
      ),
    }),
    createSortableColumn<CompanyPolicyListItem>("effectiveDate", "ວັນທີມີຜົນບັງຄັບໃຊ້", {
      size: 140,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDate(row.original.effectiveDate)}
        </span>
      ),
    }),
    createSortableColumn<CompanyPolicyListItem>("status", "ສະຖານະ", {
      size: 100,
      cell: ({ row }) => (
        <Badge variant={statusBadgeVariant(row.original.status)}>
          {statusLabel(row.original.status)}
        </Badge>
      ),
    }),
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const r = row.original;
        const items: { label: string; onClick: () => void; icon?: ReactNode }[] = [];
        if (onView) {
          items.push({ label: "ລາຍລະອຽດ", onClick: () => onView(r), icon: <Eye className="h-4 w-4" /> });
        }
        items.push({ label: "ແກ້ໄຂ", onClick: () => onEdit(r), icon: <Pencil className="h-4 w-4" /> });
        if (r.status === "draft" && onDelete) {
          items.push({ label: "ລຶບ", onClick: () => onDelete(r), icon: <Trash2 className="h-4 w-4" /> });
        }
        if ((r.status === "draft" || r.status === "active") && onArchive) {
          items.push({
            label: "ປ່ຽນເປັນເກັບໄວ້",
            onClick: () => onArchive(r),
            icon: <FileArchive className="h-4 w-4" />,
          });
        }
        if (onDuplicate) {
          items.push({
            label: "ສຳເນົາ",
            onClick: () => onDuplicate(r),
            icon: <Copy className="h-4 w-4" />,
          });
        }
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" aria-label="ການກະທຳ">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {items.map((item) => (
                <DropdownMenuItem
                  key={item.label}
                  onClick={item.onClick}
                  className={item.label === "ລຶບ" ? "text-destructive focus:text-destructive" : undefined}
                >
                  {item.icon && <span className="mr-2">{item.icon}</span>}
                  {item.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
      size: 80,
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={data}
      isLoading={isLoading}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        const s = sorting[0];
        if (s?.id) onSortingChange(s.id, !!s.desc);
      }}
    />
  );
}
